import React from 'react';
import { DEFAULT_STROKE_WIDTH, ICON_GRID } from './taxonomy';

/**
 * PetsFostering brand icons — original monoline artwork.
 *
 * House rules for anything added here:
 *   • 24×24 grid, artwork inset ~2px so icons optically match lucide-react
 *   • stroke="currentColor", fill="none", round caps + joins
 *   • solid `fill="currentColor"` only for details that would smudge shut at 16px
 *     (paw toes, eyes, noses) — never for the primary silhouette
 *   • no hardcoded colours: the icon inherits text colour so all four themes work
 */

const BrandSvg = ({
  size = ICON_GRID,
  strokeWidth = DEFAULT_STROKE_WIDTH,
  title,
  className = '',
  children,
  ...rest
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    role={title ? 'img' : undefined}
    aria-hidden={title ? undefined : 'true'}
    focusable="false"
    {...rest}
  >
    {title ? <title>{title}</title> : null}
    {children}
  </svg>
);

/** Solid detail group — toes, eyes, noses. Ignores the outer stroke width. */
const Solid = ({ children }) => (
  <g fill="currentColor" stroke="none">
    {children}
  </g>
);

/* ------------------------------------------------------------------ *
 * Brand core
 * ------------------------------------------------------------------ */

/** Heart cradling a home — the primary brand mark. */
export const BrandMark = (props) => (
  <BrandSvg {...props}>
    <path d="M12 21.2 4.9 14.1A5.02 5.02 0 0 1 12 7a5.02 5.02 0 0 1 7.1 7.1Z" />
    <path d="M8.4 13.4 12 10.3l3.6 3.1" />
    <path d="M9.7 12.6v4.5h4.6v-4.5" />
  </BrandSvg>
);

/** Four-toe paw print. The most reusable brand asset in the library. */
export const Paw = (props) => (
  <BrandSvg {...props}>
    <ellipse cx="6.6" cy="10" rx="2.05" ry="2.75" transform="rotate(-22 6.6 10)" />
    <ellipse cx="10.4" cy="6.9" rx="2.05" ry="2.9" transform="rotate(-8 10.4 6.9)" />
    <ellipse cx="14.6" cy="6.9" rx="2.05" ry="2.9" transform="rotate(8 14.6 6.9)" />
    <ellipse cx="18.4" cy="10" rx="2.05" ry="2.75" transform="rotate(22 18.4 10)" />
    <path d="M12 12.4c3.35 0 6.05 2.5 6.05 5.05 0 2.15-1.85 3.45-4.05 3.1a12 12 0 0 0-4 0c-2.2.35-4.05-.95-4.05-3.1 0-2.55 2.7-5.05 6.05-5.05Z" />
  </BrandSvg>
);

/** Heart holding a paw — "loved pet", favourites, saved caregivers. */
export const PawHeart = (props) => (
  <BrandSvg {...props}>
    <path d="M12 21 5.3 14.3A4.74 4.74 0 0 1 12 7.6a4.74 4.74 0 0 1 6.7 6.7L12 21Z" />
    <Solid>
      <circle cx="10.1" cy="11.6" r="0.72" />
      <circle cx="11.45" cy="10.8" r="0.78" />
      <circle cx="12.9" cy="10.9" r="0.78" />
      <circle cx="14.1" cy="11.8" r="0.72" />
      <path d="M12 13.1c1.45 0 2.55 1.05 2.55 2.2 0 .95-.8 1.5-1.75 1.35a5.4 5.4 0 0 0-1.6 0c-.95.15-1.75-.4-1.75-1.35 0-1.15 1.1-2.2 2.55-2.2Z" />
    </Solid>
  </BrandSvg>
);

/** Symmetric heart. Favourites, wishlists, donations, "with love" copy. */
export const Heart = (props) => (
  <BrandSvg {...props}>
    <path d="M12 20.9 5.35 14.25A4.7 4.7 0 0 1 12 7.6a4.7 4.7 0 0 1 6.65 6.65L12 20.9Z" />
  </BrandSvg>
);

/* ------------------------------------------------------------------ *
 * Pets
 * ------------------------------------------------------------------ */

/** Friendly dog face, front view. */
export const Dog = (props) => (
  <BrandSvg {...props}>
    <path d="M12 20.9c-3.75 0-6.45-2.6-6.45-6.15C5.55 10.7 8.45 7.4 12 7.4s6.45 3.3 6.45 7.35c0 3.55-2.7 6.15-6.45 6.15Z" />
    <path d="M5.9 9.4C4.15 8.5 2.7 9.6 2.7 11.9c0 2.5 1.35 4.4 3.15 4.9" />
    <path d="M18.1 9.4c1.75-.9 3.2.2 3.2 2.5 0 2.5-1.35 4.4-3.15 4.9" />
    <path d="M12 18.85v.6" />
    <path d="M12 19.45c-.55.85-1.9.85-2.45 0" />
    <path d="M12 19.45c.55.85 1.9.85 2.45 0" />
    <Solid>
      <circle cx="9.6" cy="13.8" r="0.95" />
      <circle cx="14.4" cy="13.8" r="0.95" />
      <path d="M10.85 16.2h2.3c.55 0 .87.6.57 1.05l-1.15 1.7a.7.7 0 0 1-1.14 0l-1.15-1.7c-.3-.45.02-1.05.57-1.05Z" />
    </Solid>
  </BrandSvg>
);

/** Cat face with pointed ears and whiskers, front view. */
export const Cat = (props) => (
  <BrandSvg {...props}>
    <path d="M12 20.8c-3.6 0-6.25-2.5-6.25-5.95 0-3.6 2.8-6.4 6.25-6.4s6.25 2.8 6.25 6.4c0 3.45-2.65 5.95-6.25 5.95Z" />
    <path d="M6.85 10.35 5.4 4.6l5.05 2.55" />
    <path d="M17.15 10.35 18.6 4.6l-5.05 2.55" />
    <path d="M6.2 15.1H2.9" />
    <path d="M6.4 17.05l-3.1 1" />
    <path d="M17.8 15.1h3.3" />
    <path d="M17.6 17.05l3.1 1" />
    <path d="M12 18.65v.55" />
    <path d="M12 19.2c-.5.75-1.7.75-2.2 0" />
    <path d="M12 19.2c.5.75 1.7.75 2.2 0" />
    <Solid>
      <circle cx="9.7" cy="14.2" r="0.95" />
      <circle cx="14.3" cy="14.2" r="0.95" />
      <path d="M11.1 16.5h1.8c.47 0 .74.52.47.91l-.9 1.3a.6.6 0 0 1-.98 0l-.9-1.3c-.27-.39 0-.91.51-.91Z" />
    </Solid>
  </BrandSvg>
);

/** Dog bone. Treats, rewards, pet supplies, playful empty states. */
export const Bone = (props) => (
  <BrandSvg {...props}>
    <Solid>
      <rect x="7" y="10.4" width="10" height="3.2" />
      <circle cx="7.1" cy="10.3" r="2.5" />
      <circle cx="7.1" cy="13.7" r="2.5" />
      <circle cx="16.9" cy="10.3" r="2.5" />
      <circle cx="16.9" cy="13.7" r="2.5" />
    </Solid>
  </BrandSvg>
);

/** Collar with a heart-shaped ID tag. Pet profile, microchip, identity. */
export const CollarTag = (props) => (
  <BrandSvg {...props}>
    <ellipse cx="12" cy="8.4" rx="7.6" ry="3.4" />
    <rect x="3.3" y="7.1" width="2.6" height="2.6" rx="0.8" />
    <path d="M12 11.8v1.1" />
    <path d="M12 19.6 8.9 16.5a2.19 2.19 0 0 1 3.1-3.1 2.19 2.19 0 0 1 3.1 3.1L12 19.6Z" />
  </BrandSvg>
);

/* ------------------------------------------------------------------ *
 * Places & care
 * ------------------------------------------------------------------ */

/** Pet house with chimney and arched door. */
export const PetHouse = (props) => (
  <BrandSvg {...props}>
    <path d="M2.6 11.7 12 3.7l9.4 8" />
    <path d="M4.9 10.5v9.8h14.2v-9.8" />
    <path d="M16.8 6.6V4.2h2.4v4.4" />
    <path d="M9.7 20.3v-3.7a2.3 2.3 0 0 1 4.6 0v3.7" />
    <path d="M3.4 20.3h17.2" />
  </BrandSvg>
);

/** Home + heart + paw. The fostering/adoption concept in one mark. */
export const FosterHome = (props) => (
  <BrandSvg {...props}>
    <path d="M2.6 11.4 12 3.6l9.4 7.8" />
    <path d="M5 10.3v10h14v-10" />
    <path d="M3.4 20.3h17.2" />
    <path d="M12 12.9 9.75 10.65a1.59 1.59 0 0 1 2.25-2.25 1.59 1.59 0 0 1 2.25 2.25L12 12.9Z" />
    <Solid>
      <circle cx="9.95" cy="15" r="0.6" />
      <circle cx="11.3" cy="14.4" r="0.64" />
      <circle cx="12.7" cy="14.4" r="0.64" />
      <circle cx="14.05" cy="15" r="0.6" />
      <path d="M12 16.1c1.5 0 2.7 1.15 2.7 2.35 0 1-.85 1.6-1.85 1.42a5.9 5.9 0 0 0-1.7 0c-1 .18-1.85-.42-1.85-1.42 0-1.2 1.2-2.35 2.7-2.35Z" />
    </Solid>
  </BrandSvg>
);

/** Two hands cradling a paw. Trust, donations, "in safe hands". */
export const CaringHands = (props) => (
  <BrandSvg {...props}>
    <path d="M10.9 20.8H7.5a3.4 3.4 0 0 1-3.4-3.4v-4a1.45 1.45 0 0 1 2.9 0v2.5" />
    <path d="M13.1 20.8h3.4a3.4 3.4 0 0 0 3.4-3.4v-4a1.45 1.45 0 0 0-2.9 0v2.5" />
    <path d="M10.9 20.8h2.2" />
    <Solid>
      <circle cx="9.9" cy="6.6" r="0.62" />
      <circle cx="11.25" cy="5.95" r="0.66" />
      <circle cx="12.75" cy="5.95" r="0.66" />
      <circle cx="14.1" cy="6.6" r="0.62" />
      <path d="M12 7.65c1.55 0 2.8 1.2 2.8 2.45 0 1.03-.88 1.65-1.9 1.47a6 6 0 0 0-1.8 0c-1.02.18-1.9-.44-1.9-1.47 0-1.25 1.25-2.45 2.8-2.45Z" />
    </Solid>
  </BrandSvg>
);

/** Map pin with a paw. Caregiver location, service area, map markers. */
export const LocationPaw = (props) => (
  <BrandSvg {...props}>
    <path d="M12 21.5c4.4-4.6 6.9-7.7 6.9-11.1A6.9 6.9 0 1 0 5.1 10.4c0 3.4 2.5 6.5 6.9 11.1Z" />
    <Solid>
      <circle cx="10.2" cy="8.9" r="0.6" />
      <circle cx="11.35" cy="8.3" r="0.64" />
      <circle cx="12.65" cy="8.3" r="0.64" />
      <circle cx="13.8" cy="8.9" r="0.6" />
      <path d="M12 9.5c1.4 0 2.55 1.1 2.55 2.25 0 .95-.8 1.5-1.75 1.35a5.6 5.6 0 0 0-1.6 0c-.95.15-1.75-.4-1.75-1.35 0-1.15 1.15-2.25 2.55-2.25Z" />
    </Solid>
  </BrandSvg>
);

/* ------------------------------------------------------------------ *
 * Services
 * ------------------------------------------------------------------ */

/** Vet bag with a medical cross. Veterinary visits, health records. */
export const VetKit = (props) => (
  <BrandSvg {...props}>
    <rect x="3" y="8.4" width="18" height="11.6" rx="2.4" />
    <path d="M9.2 8.4V6.9a1.5 1.5 0 0 1 1.5-1.5h2.6a1.5 1.5 0 0 1 1.5 1.5v1.5" />
    <path d="M12 11.4v5.6" />
    <path d="M9.2 14.2h5.6" />
  </BrandSvg>
);

/** Food bowl with kibble. Feeding schedule, meals, care instructions. */
export const FeedingBowl = (props) => (
  <BrandSvg {...props}>
    <ellipse cx="12" cy="14" rx="9" ry="2.1" />
    <path d="M3 14c0 3.6 4 6.6 9 6.6s9-3 9-6.6" />
    <circle cx="9.4" cy="11.2" r="1.15" />
    <circle cx="12.2" cy="10.4" r="1.15" />
    <circle cx="14.9" cy="11.4" r="1.15" />
  </BrandSvg>
);

/** Grooming comb with bubbles. Bathing, grooming, styling. */
export const GroomingComb = (props) => (
  <BrandSvg {...props}>
    <rect x="3.4" y="5.4" width="17.2" height="4.4" rx="1.8" />
    <path d="M6 9.8v6.6" />
    <path d="M9 9.8v6.6" />
    <path d="M12 9.8v6.6" />
    <path d="M15 9.8v6.6" />
    <path d="M18 9.8v6.6" />
    <circle cx="18.4" cy="19.2" r="1.6" />
    <circle cx="14.2" cy="20.4" r="1" />
  </BrandSvg>
);

/** Sun with a paw. Daycare, daytime slots, "open now". */
export const DayCare = (props) => (
  <BrandSvg {...props}>
    <circle cx="12" cy="12" r="5" />
    <path d="M12 2.4v2" />
    <path d="M12 19.6v2" />
    <path d="M4.6 4.6 6 6" />
    <path d="M18 18l1.4 1.4" />
    <path d="M2.4 12h2" />
    <path d="M19.6 12h2" />
    <path d="M4.6 19.4 6 18" />
    <path d="M18 6l1.4-1.4" />
    <Solid>
      <circle cx="10.4" cy="10.9" r="0.5" />
      <circle cx="11.4" cy="10.4" r="0.54" />
      <circle cx="12.6" cy="10.4" r="0.54" />
      <circle cx="13.6" cy="10.9" r="0.5" />
      <path d="M12 11.5c1.2 0 2.2.95 2.2 1.9 0 .8-.7 1.28-1.5 1.15a4.8 4.8 0 0 0-1.4 0c-.8.13-1.5-.35-1.5-1.15 0-.95 1-1.9 2.2-1.9Z" />
    </Solid>
  </BrandSvg>
);

/** Crescent moon with a paw. Overnight care, boarding, quiet hours. */
export const NightCare = (props) => (
  <BrandSvg {...props}>
    <path d="M19.4 13.9A8 8 0 0 1 9.3 3.8 8 8 0 1 0 19.4 13.9Z" />
    <path d="M17.2 3.4v2.2" />
    <path d="M16.1 4.5h2.2" />
    <path d="M20.4 8.2v1.6" />
    <path d="M19.6 9h1.6" />
    <Solid>
      <circle cx="6.7" cy="13.9" r="0.48" />
      <circle cx="7.6" cy="13.45" r="0.52" />
      <circle cx="8.65" cy="13.45" r="0.52" />
      <circle cx="9.55" cy="13.9" r="0.48" />
      <path d="M8.1 14.5c1.1 0 2 .85 2 1.72 0 .74-.62 1.16-1.36 1.05a4.4 4.4 0 0 0-1.3 0c-.74.11-1.36-.31-1.36-1.05 0-.87.9-1.72 2-1.72Z" />
    </Solid>
  </BrandSvg>
);

/* ------------------------------------------------------------------ *
 * Journey
 * ------------------------------------------------------------------ */

/** Calendar with a paw. Bookings, availability, pet schedules. */
export const CalendarPaw = (props) => (
  <BrandSvg {...props}>
    <rect x="3" y="6" width="18" height="15" rx="2.6" />
    <path d="M3 10.4h18" />
    <path d="M8 3.4v3.2" />
    <path d="M16 3.4v3.2" />
    <Solid>
      <circle cx="9.6" cy="13.9" r="0.72" />
      <circle cx="11.15" cy="13.15" r="0.78" />
      <circle cx="12.85" cy="13.15" r="0.78" />
      <circle cx="14.4" cy="13.9" r="0.72" />
      <path d="M12 14.9c1.75 0 3.15 1.35 3.15 2.75 0 1.15-1 1.85-2.15 1.65a6.8 6.8 0 0 0-2 0c-1.15.2-2.15-.5-2.15-1.65 0-1.4 1.4-2.75 3.15-2.75Z" />
    </Solid>
  </BrandSvg>
);

/** Speech bubble with a paw. Messages, pet-parent chat, support. */
export const ChatPaw = (props) => (
  <BrandSvg {...props}>
    <path d="M6.4 5h11.2a2.8 2.8 0 0 1 2.8 2.8v5.8a2.8 2.8 0 0 1-2.8 2.8H9.6l-4.2 3.4v-3.4a2.8 2.8 0 0 1-1.8-2.8V7.8A2.8 2.8 0 0 1 6.4 5Z" />
    <Solid>
      <circle cx="9.5" cy="9.9" r="0.7" />
      <circle cx="11.6" cy="9.4" r="0.75" />
      <circle cx="13.7" cy="9.9" r="0.7" />
      <path d="M11.6 10.9c1.6 0 2.9 1.2 2.9 2.45 0 1.02-.9 1.65-1.95 1.45a6.4 6.4 0 0 0-1.9 0c-1.05.2-1.95-.43-1.95-1.45 0-1.25 1.3-2.45 2.9-2.45Z" />
    </Solid>
  </BrandSvg>
);

/** Shield with a paw. Verified caregiver, insurance, safety promise. */
export const ShieldPaw = (props) => (
  <BrandSvg {...props}>
    <path d="M12 21.4c4.6-1.7 7.4-5.4 7.4-9.9V5.9L12 3.2 4.6 5.9v5.6c0 4.5 2.8 8.2 7.4 9.9Z" />
    <Solid>
      <circle cx="10.1" cy="10.1" r="0.62" />
      <circle cx="11.3" cy="9.45" r="0.66" />
      <circle cx="12.7" cy="9.45" r="0.66" />
      <circle cx="13.9" cy="10.1" r="0.62" />
      <path d="M12 10.8c1.5 0 2.7 1.15 2.7 2.35 0 1-.85 1.6-1.85 1.42a5.9 5.9 0 0 0-1.7 0c-1 .18-1.85-.42-1.85-1.42 0-1.2 1.2-2.35 2.7-2.35Z" />
    </Solid>
  </BrandSvg>
);

/** Award rosette with a paw. Badges, top caregiver, milestones. */
export const BadgePaw = (props) => (
  <BrandSvg {...props}>
    <circle cx="12" cy="9.4" r="6.4" />
    <path d="M8.4 14.9 6.6 21.4 12 18.6l5.4 2.8-1.8-6.5" />
    <Solid>
      <circle cx="10.2" cy="8.1" r="0.58" />
      <circle cx="11.35" cy="7.5" r="0.62" />
      <circle cx="12.65" cy="7.5" r="0.62" />
      <circle cx="13.8" cy="8.1" r="0.58" />
      <path d="M12 8.7c1.38 0 2.5 1.05 2.5 2.15 0 .92-.78 1.45-1.7 1.3a5.5 5.5 0 0 0-1.6 0c-.92.15-1.7-.38-1.7-1.3 0-1.1 1.12-2.15 2.5-2.15Z" />
    </Solid>
  </BrandSvg>
);

/**
 * Registry of every brand icon, keyed by the id used in icon-metadata.json.
 * Order here is only for readability — the gallery sorts by metadata.
 */
export const BRAND_ICONS = {
  'brand-mark': BrandMark,
  paw: Paw,
  'paw-heart': PawHeart,
  heart: Heart,
  dog: Dog,
  cat: Cat,
  bone: Bone,
  'collar-tag': CollarTag,
  'pet-house': PetHouse,
  'foster-home': FosterHome,
  'caring-hands': CaringHands,
  'location-paw': LocationPaw,
  'vet-kit': VetKit,
  'feeding-bowl': FeedingBowl,
  'grooming-comb': GroomingComb,
  'day-care': DayCare,
  'night-care': NightCare,
  'calendar-paw': CalendarPaw,
  'chat-paw': ChatPaw,
  'shield-paw': ShieldPaw,
  'badge-paw': BadgePaw,
};

export const BRAND_ICON_IDS = Object.keys(BRAND_ICONS);

export default BRAND_ICONS;
